/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS MCP Server Registry Service
 * 
 * API client for VPS MCP Server Registry endpoints.
 * Uses shared schema types and handleApiResponse for consistent error handling.
 * 
 * For simple GET endpoints, components can use createQueryFn directly:
 *   import { createQueryFn } from '@shared/lib/api';
 *   const { data } = useQuery({
 *     queryKey: MCP_QUERY_KEYS.stats,
 *     queryFn: createQueryFn<McpRegistryStats>(MCP_ENDPOINTS.stats),
 *   });
 */

import { authenticatedFetch, createQueryFn, createMutationFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  McpServerEntry,
  McpServerCategory,
  McpServerCapabilities,
  McpServerSearchResult,
  McpRegistryStats,
  McpServerStatus,
} from '@shared/schema/mcpServers';

// ============================================================================
// API Endpoints - Centralized endpoint definitions
// ============================================================================

export const MCP_ENDPOINTS = {
  list: '/api/mcp-servers',
  stats: '/api/mcp-servers/stats',
  categories: '/api/mcp-servers/categories',
  indexRebuild: '/api/mcp-servers/index/rebuild',
  search: '/api/mcp-servers/search',
  byCategory: (category: string) => `/api/mcp-servers/by-category/${category}`,
  byTool: (tool: string) => `/api/mcp-servers/by-tool/${tool}`,
  withSkills: '/api/mcp-servers/with-skills',
  detail: (id: string) => `/api/mcp-servers/${id}`,
  config: (id: string) => `/api/mcp-servers/${id}/config`,
  docs: (id: string) => `/api/mcp-servers/${id}/docs`,
  verify: (id: string) => `/api/mcp-servers/${id}/verify`,
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const MCP_QUERY_KEYS = {
  all: ['mcp-servers'] as const,
  list: ['mcp-servers', 'list'] as const,
  stats: ['mcp-servers', 'stats'] as const,
  categories: ['mcp-servers', 'categories'] as const,
  detail: (id: string) => ['mcp-servers', 'detail', id] as const,
  config: (id: string) => ['mcp-servers', 'config', id] as const,
  search: (query: string) => ['mcp-servers', 'search', query] as const,
} as const;

// Re-export types for convenience
export type {
  McpServerEntry,
  McpServerCategory,
  McpServerCapabilities,
  McpRegistryStats as McpRegistryStatsResponse,
};

export interface SearchMcpServersResponse {
  results: McpServerSearchResult[];
  totalCount: number;
  query?: string;
}

// ============================================================================
// Query/Mutation Function Factories - For direct use in components
// ============================================================================

/** 
 * Query function for MCP registry stats - use with useQuery
 * 
 * The API returns { registry: McpRegistryStats, index: {...} }, so we
 * need to extract the registry stats from the nested response.
 */
export const mcpStatsQueryFn = async (): Promise<McpRegistryStats> => {
  const response = await authenticatedFetch(MCP_ENDPOINTS.stats);
  const data = await handleApiResponse<{ registry: McpRegistryStats | null }>(response);
  // Return registry stats or empty stats if not loaded
  return data.registry ?? {
    totalServers: 0,
    byStatus: { active: 0, deprecated: 0, unavailable: 0, pending: 0 },
    byCategory: {},
    totalTools: 0,
    totalResources: 0,
    withRelatedSkills: 0,
    lastUpdated: new Date().toISOString(),
  };
};

/** Query function for MCP categories - use with useQuery */
export const mcpCategoriesQueryFn = createQueryFn<{ categories: McpServerCategory[] }>(MCP_ENDPOINTS.categories);

/** Mutation function for rebuilding MCP index - use with useMutation */
export const rebuildMcpIndexMutationFn = createMutationFn<{ message: string }, void>(MCP_ENDPOINTS.indexRebuild, 'POST');

// ============================================================================
// Service Functions - For complex operations with transformations
// ============================================================================

/**
 * List all registered MCP servers
 */
export async function listMcpServers(): Promise<McpServerEntry[]> {
  const response = await authenticatedFetch('/api/mcp-servers');
  // API returns servers array directly in data field
  const data = await handleApiResponse<McpServerEntry[]>(response);
  // Ensure we always return an array (React Query requires non-undefined)
  return data ?? [];
}

/**
 * Search MCP servers
 */
export async function searchMcpServers(
  query?: string,
  options?: {
    category?: McpServerCategory;
    status?: 'active' | 'deprecated' | 'unavailable';
    hasTool?: string;
    limit?: number;
  }
): Promise<SearchMcpServersResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (options?.category) params.set('category', options.category);
  if (options?.status) params.set('status', options.status);
  if (options?.hasTool) params.set('hasTool', options.hasTool);
  if (options?.limit) params.set('limit', options.limit.toString());

  const response = await authenticatedFetch(`/api/mcp-servers/search?${params}`);
  return handleApiResponse<SearchMcpServersResponse>(response);
}

/**
 * Get a specific MCP server
 */
export async function getMcpServer(id: string): Promise<McpServerEntry> {
  const response = await authenticatedFetch(`/api/mcp-servers/${encodeURIComponent(id)}`);
  return handleApiResponse<McpServerEntry>(response);
}

/**
 * Get MCP server configuration
 */
export async function getMcpServerConfig(id: string): Promise<Record<string, any>> {
  const response = await authenticatedFetch(`/api/mcp-servers/${encodeURIComponent(id)}/config`);
  return handleApiResponse<Record<string, any>>(response);
}

/**
 * Get MCP server documentation
 */
export async function getMcpServerDocs(id: string): Promise<string> {
  const response = await authenticatedFetch(`/api/mcp-servers/${encodeURIComponent(id)}/docs`);
  const data = await handleApiResponse<{ content: string }>(response);
  return data.content;
}

/**
 * Verify MCP server availability
 */
export async function verifyMcpServer(id: string): Promise<{
  available: boolean;
  message: string;
  lastVerified: string;
}> {
  const response = await authenticatedFetch(`/api/mcp-servers/${encodeURIComponent(id)}/verify`, {
    method: 'POST',
  });
  return handleApiResponse<{
    available: boolean;
    message: string;
    lastVerified: string;
  }>(response);
}

/**
 * Get available MCP server categories
 */
export async function getMcpServerCategories(): Promise<Array<{
  id: McpServerCategory;
  name: string;
  description: string;
  count: number;
}>> {
  const response = await authenticatedFetch('/api/mcp-servers/categories');
  const data = await handleApiResponse<{ categories: Array<{
    id: McpServerCategory;
    name: string;
    description: string;
    count: number;
  }> }>(response);
  return data.categories;
}

/**
 * Get MCP registry statistics
 */
export async function getMcpRegistryStats(): Promise<McpRegistryStats> {
  const response = await authenticatedFetch('/api/mcp-servers/stats');
  return handleApiResponse<McpRegistryStats>(response);
}

/**
 * Get MCP servers by category
 */
export async function getMcpServersByCategory(category: McpServerCategory): Promise<McpServerEntry[]> {
  const response = await authenticatedFetch(`/api/mcp-servers/by-category/${category}`);
  const data = await handleApiResponse<{ servers: McpServerEntry[] }>(response);
  return data.servers;
}

/**
 * Get MCP servers that provide a specific tool
 */
export async function getMcpServersByTool(tool: string): Promise<McpServerEntry[]> {
  const response = await authenticatedFetch(`/api/mcp-servers/by-tool/${encodeURIComponent(tool)}`);
  const data = await handleApiResponse<{ servers: McpServerEntry[] }>(response);
  return data.servers;
}

/**
 * Get MCP servers with related skills
 */
export async function getMcpServersWithSkills(): Promise<Array<McpServerEntry & { skills: string[] }>> {
  const response = await authenticatedFetch('/api/mcp-servers/with-skills');
  const data = await handleApiResponse<{ servers: Array<McpServerEntry & { skills: string[] }> }>(response);
  return data.servers;
}

/**
 * Rebuild MCP server index
 */
export async function rebuildMcpIndex(): Promise<{ totalServers: number }> {
  const response = await authenticatedFetch('/api/mcp-servers/index/rebuild', {
    method: 'POST',
  });
  return handleApiResponse<{ totalServers: number }>(response);
}
